import { EnemyType } from './GameTypes';
import { GAME_CONSTANTS } from '../utils/Constants';

export interface EnemyTypeData {
  baseHp: number;
  baseSpeed: number;
  goalDamage: number;
  isFlying: boolean;
  isGhost: boolean;
  physicalResistance: number; // 0-1, percentage reduction
  elementalVulnerability: number; // Multiplier (1.15 for ghosts)
  evasionChance: number; // 0-1, chance to dodge attacks (0.3 = 30%)
}

export const ENEMY_TYPE_DATA: Record<EnemyType, EnemyTypeData> = {
  ground: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE,
    baseSpeed: 1.0,
    goalDamage: 1,
    isFlying: false,
    isGhost: false,
    physicalResistance: 0,
    elementalVulnerability: 1.0,
    evasionChance: 0, // No evasion
  },
  runner: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE * 0.7, // Lower HP
    baseSpeed: 2.5, // 200-300% speed
    goalDamage: 2,
    isFlying: false,
    isGhost: false,
    physicalResistance: 0,
    elementalVulnerability: 1.0,
    evasionChance: 0,
  },
  armored: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE * 1.5,
    baseSpeed: 1.0,
    goalDamage: 3,
    isFlying: false,
    isGhost: false,
    physicalResistance: 0.4, // 30-50% reduction
    elementalVulnerability: 1.0,
    evasionChance: 0,
  },
  splitter: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE * 3.0, // 150% more HP (was 1.2x, now 3.0x = 1.2 * 2.5)
    baseSpeed: 1.0,
    goalDamage: 1,
    isFlying: false,
    isGhost: false,
    physicalResistance: 0,
    elementalVulnerability: 1.0,
    evasionChance: 0,
  },
  healer: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE * 0.8,
    baseSpeed: 1.0,
    goalDamage: 1,
    isFlying: false,
    isGhost: false,
    physicalResistance: 0,
    elementalVulnerability: 1.0,
    evasionChance: 0,
  },
  ghost: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE,
    baseSpeed: 0.36, // 70% slower
    goalDamage: 1,
    isFlying: false,
    isGhost: true,
    physicalResistance: 0.65, // 65% physical resistance
    elementalVulnerability: 1.3, // +30% elemental damage (more vulnerable to elemental)
    evasionChance: 0, // No evasion (resistance is their defense)
  },
  flying: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE * 0.8, // Lower HP since they bypass walls
    baseSpeed: 0.39, // 70% slower
    goalDamage: 1,
    isFlying: true,
    isGhost: false,
    physicalResistance: 0.5, // 50% physical resistance (can still be attacked, just more resistant)
    elementalVulnerability: 1.2, // +20% elemental damage
    evasionChance: 0.3, // 30% chance to dodge attacks (flying = hard to hit)
  },
  elite: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE * 2.0, // +100-250% HP
    baseSpeed: 1.15, // +15% speed
    goalDamage: 2, // +1 additional
    isFlying: false,
    isGhost: false,
    physicalResistance: 0,
    elementalVulnerability: 1.0,
    evasionChance: 0,
  },
  miniboss: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE * GAME_CONSTANTS.MINIBOSS_HP_MULTIPLIER,
    baseSpeed: 0.8, // Slower
    goalDamage: GAME_CONSTANTS.MINIBOSS_DAMAGE,
    isFlying: false,
    isGhost: false,
    physicalResistance: 0.1,
    elementalVulnerability: 1.0,
    evasionChance: 0,
  },
  boss: {
    baseHp: GAME_CONSTANTS.ENEMY_HP_BASE * GAME_CONSTANTS.BOSS_HP_MULTIPLIER,
    baseSpeed: 0.6, // Slow
    goalDamage: GAME_CONSTANTS.BOSS_DAMAGE,
    isFlying: false,
    isGhost: false,
    physicalResistance: 0.2,
    elementalVulnerability: 1.0,
    evasionChance: 0.1, // 10% base evasion (will scale with wave level)
  },
};

export function calculateEnemyHP(enemyType: EnemyType, waveNumber: number, isSurvival: boolean = false): number {
  const baseData = ENEMY_TYPE_DATA[enemyType];
  
  let hp = baseData.baseHp;
  
  // Hybrid scaling: 8% until wave 50, then 6% until wave 150, then 3% after
  // Option B: Faster early, slower late
  for (let i = 1; i < waveNumber; i++) {
    if (i <= 50) {
      // Waves 1-50: 8% per wave
      hp *= 1.08;
    } else if (i <= 150) {
      // Waves 51-150: 6% per wave
      hp *= 1.06;
    } else {
      // Waves 151+: 3% per wave
      hp *= 1.03;
    }
  }
  
  // Survival mode: Apply additional multiplier on top of hybrid scaling
  if (isSurvival) {
    hp *= GAME_CONSTANTS.ENEMY_HP_SURVIVAL_MULTIPLIER;
  }
  
  return Math.floor(hp);
}

export function calculateEnemyCount(waveNumber: number, isSurvival: boolean = false): number {
  // Hybrid scaling: Matches HP scaling pattern, capped at 100 by wave 150
  // Option B: Faster early, slower late
  let count = 15; // Base count (wave 1)
  
  // Apply hybrid scaling matching HP pattern
  for (let i = 1; i < waveNumber; i++) {
    if (i <= 50) {
      // Waves 1-50: 8% per wave (faster early growth)
      count *= 1.08;
    } else if (i <= 150) {
      // Waves 51-150: 6% per wave (slower late growth)
      count *= 1.06;
    } else {
      // Waves 151+: Stay at cap (no further growth)
      // Already at cap, no need to multiply
      break;
    }
    
    // Cap at 100 enemies (reached by wave 50)
    if (count >= 100) {
      count = 100;
      break;
    }
  }
  
  // Survival mode: Apply additional multiplier on top of hybrid scaling
  if (isSurvival) {
    count *= GAME_CONSTANTS.ENEMY_COUNT_SURVIVAL_MULTIPLIER;
    // Still cap at 100 even in survival mode
    if (count > 100) {
      count = 100;
    }
  }
  
  return Math.floor(count);
}

